import { computed, unref, type Ref } from "vue";
import { useUsageStats } from "@/composables/billing/useUsageStats";
import { getDateRangeForPeriod } from "./useReportDateFilters";
import { formatServiceKey, formatUsageQuantity } from "@/utils/usageFormat";
import type {
  CostCategory,
  CostCategoryKey,
  CostChannel,
  CostTypeRow,
  UsageStatItem,
  UsageStatsResponse,
} from "@/dtos/CostMetrics.dto";

// Usage-based (pay-as-you-go) costs only — seat subscription and add-on fees are excluded.

interface CostTypeDef {
  key: string;
  label: string;
  // Legacy service keys are folded into their current equivalent
  serviceKeys: string[];
  unit: string;
  unitPlural: string;
  isMinutes?: boolean;
  // Split this type's cost by usage metadata.channel
  splitByChannel?: boolean;
}

interface CostCategoryDef {
  key: Exclude<CostCategoryKey, "other">;
  label: string;
  icon: string;
  color: string;
  types: CostTypeDef[];
  // How the category card summarises usage
  summary: "minutes" | "units" | "events";
  summaryUnit?: [string, string];
}

const COST_CATEGORIES: CostCategoryDef[] = [
  {
    key: "calling",
    label: "Calling",
    icon: "mdi:phone-outline",
    color: "#10b981",
    summary: "minutes",
    types: [
      {
        key: "call_minutes",
        label: "Call minutes",
        serviceKeys: ["us_call_minute", "call_minute", "call_minutes"],
        unit: "min",
        unitPlural: "min",
        isMinutes: true,
      },
      {
        key: "call_recording",
        label: "Call recording",
        serviceKeys: ["call_recording_minute", "call_recording"],
        unit: "min",
        unitPlural: "min",
        isMinutes: true,
      },
    ],
  },
  {
    key: "messaging",
    label: "SMS / MMS",
    icon: "mdi:message-text-outline",
    color: "#f59e0b",
    summary: "units",
    summaryUnit: ["segment", "segments"],
    types: [
      {
        key: "sms_outbound",
        label: "Outbound SMS",
        serviceKeys: ["sms_outbound", "sms_segment"],
        unit: "segment",
        unitPlural: "segments",
      },
      {
        key: "sms_inbound",
        label: "Inbound SMS",
        serviceKeys: ["sms_inbound"],
        unit: "segment",
        unitPlural: "segments",
      },
      {
        key: "mms_inbound",
        label: "Inbound MMS",
        serviceKeys: ["mms_inbound"],
        unit: "segment",
        unitPlural: "segments",
      },
    ],
  },
  {
    key: "enrichment",
    label: "Enrichment",
    icon: "mdi:account-search-outline",
    color: "#3b82f6",
    summary: "units",
    summaryUnit: ["match", "matches"],
    types: [
      {
        key: "email_enrichment",
        label: "Email enrichment",
        serviceKeys: ["verified_email_enrichment", "email_enrichment", "enrichment_email"],
        unit: "match",
        unitPlural: "matches",
      },
      {
        key: "phone_enrichment",
        label: "Phone enrichment",
        serviceKeys: ["mobile_number_enrichment", "phone_enrichment", "enrichment_mobile"],
        unit: "match",
        unitPlural: "matches",
      },
    ],
  },
  {
    key: "ai",
    label: "AI",
    icon: "mdi:creation",
    color: "#8b5cf6",
    summary: "events",
    summaryUnit: ["request", "requests"],
    types: [
      {
        key: "ai_prospecting_agent",
        label: "AI prospecting agent",
        serviceKeys: ["ai_prospecting_agent"],
        unit: "message",
        unitPlural: "messages",
      },
      {
        key: "ai_content_generation",
        label: "AI reply, compose & follow-up",
        serviceKeys: ["ai_content_generation"],
        unit: "generation",
        unitPlural: "generations",
        splitByChannel: true,
      },
      {
        key: "ai_sequence_generation",
        label: "AI sequence generation",
        serviceKeys: ["ai_sequence_generation"],
        unit: "step",
        unitPlural: "steps",
      },
      {
        key: "ai_call_summary",
        label: "AI call summary",
        serviceKeys: ["ai_call_summary", "ai_summary"],
        unit: "summary",
        unitPlural: "summaries",
      },
      {
        key: "ai_call_assistant",
        label: "AI call assistant",
        serviceKeys: ["ai_call_assistant_minute"],
        unit: "min",
        unitPlural: "min",
        isMinutes: true,
      },
      {
        key: "ai_chat",
        label: "AI Insights chat",
        serviceKeys: ["ai_chat_message", "ai_insights"],
        unit: "message",
        unitPlural: "messages",
      },
    ],
  },
];

// ai_draft has a rate but is never recorded, so it is only shown if it ever carries cost
const OTHER_CATEGORY = {
  key: "other" as const,
  label: "Other",
  icon: "mdi:dots-horizontal",
  color: "#9ca3af",
};

const CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  linkedin: "LinkedIn",
  call: "Call",
  sms: "SMS",
  whatsapp: "WhatsApp",
  all: "Multi-channel",
};

// Which cost types count towards each outreach channel (for cost per meeting by channel)
const CHANNEL_COST_SOURCES: Record<CostChannel, { types: string[]; aiChannels: string[] }> = {
  call: {
    types: [
      "call_minutes",
      "call_recording",
      "sms_outbound",
      "sms_inbound",
      "mms_inbound",
      "phone_enrichment",
      "ai_call_summary",
      "ai_call_assistant",
    ],
    aiChannels: ["call", "sms"],
  },
  email: { types: ["email_enrichment"], aiChannels: ["email"] },
  linkedin: { types: ["ai_prospecting_agent"], aiChannels: ["linkedin"] },
};

// "All" has no range on the metrics tabs; the usage API defaults to the current month without one.
const ALL_TIME_START = new Date(2020, 0, 1);

export interface CostDateParams {
  startDate: string;
  endDate: string;
  isAllTime: boolean;
}

export function getCostDateParams(
  period: string | null | undefined,
  dateRange: Array<Date | string> | null | undefined
): CostDateParams {
  if (period && period !== "all") {
    const range = getDateRangeForPeriod(period);
    if (range) {
      return {
        startDate: range[0].toISOString(),
        endDate: range[1].toISOString(),
        isAllTime: false,
      };
    }
  }

  // Custom range from the date picker: cover the whole of both the first and last day
  if (period !== "all" && dateRange?.[0] && dateRange?.[1]) {
    const start = new Date(dateRange[0]);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange[1]);
    end.setHours(23, 59, 59, 999);
    return { startDate: start.toISOString(), endDate: end.toISOString(), isAllTime: false };
  }

  return {
    startDate: ALL_TIME_START.toISOString(),
    endDate: new Date().toISOString(),
    isAllTime: true,
  };
}

function formatCount(n: number, [singular, plural]: [string, string]) {
  const rounded = Math.round(n);
  return `${rounded.toLocaleString()} ${rounded === 1 ? singular : plural}`;
}

function formatMinutes(units: number) {
  return formatUsageQuantity("us_call_minute", units);
}

function buildRow(def: CostTypeDef, stats: UsageStatItem[]): CostTypeRow {
  const matches = stats.filter((s) => def.serviceKeys.includes(s.service_key));
  const units = matches.reduce((sum, s) => sum + (Number(s.total_units) || 0), 0);
  const events = matches.reduce((sum, s) => sum + (Number(s.event_count) || 0), 0);
  const cost = matches.reduce((sum, s) => sum + (Number(s.total_cost_cents) || 0), 0) / 100;

  let breakdown: CostTypeRow["breakdown"];
  if (def.splitByChannel) {
    const byChannel: Record<string, { units: number; cents: number }> = {};
    for (const s of matches) {
      for (const [channel, ch] of Object.entries(s.by_channel ?? {})) {
        byChannel[channel] = byChannel[channel] || { units: 0, cents: 0 };
        byChannel[channel].units += Number(ch.total_units) || 0;
        byChannel[channel].cents += Number(ch.total_cost_cents) || 0;
      }
    }
    breakdown = Object.entries(byChannel)
      .map(([channel, v]) => ({
        key: channel,
        label: CHANNEL_LABELS[channel] ?? formatServiceKey(channel),
        units: v.units,
        cost: v.cents / 100,
      }))
      .sort((a, b) => b.cost - a.cost);
  }

  return {
    key: def.key,
    label: def.label,
    units,
    events,
    quantity: def.isMinutes ? formatMinutes(units) : formatCount(units, [def.unit, def.unitPlural]),
    unit: def.unit,
    cost,
    unitPrice: units > 0 ? cost / units : null,
    breakdown,
  };
}

function summarise(def: CostCategoryDef, rows: CostTypeRow[]) {
  // Recording minutes overlap talk time, so only the first (talk-time) type is summed
  if (def.summary === "minutes") return `${formatMinutes(rows[0]?.units ?? 0)} talk time`;
  const total = rows.reduce((s, r) => s + (def.summary === "events" ? r.events : r.units), 0);
  return formatCount(total, def.summaryUnit ?? ["unit", "units"]);
}

export function buildCostCategories(stats: UsageStatItem[]): CostCategory[] {
  const knownKeys = new Set(COST_CATEGORIES.flatMap((c) => c.types.flatMap((t) => t.serviceKeys)));

  const categories: CostCategory[] = COST_CATEGORIES.map((def) => {
    const rows = def.types.map((t) => buildRow(t, stats));
    return {
      key: def.key,
      label: def.label,
      icon: def.icon,
      color: def.color,
      rows,
      total: rows.reduce((s, r) => s + r.cost, 0),
      usageSummary: summarise(def, rows),
    };
  });

  // Anything the backend bills that isn't mapped above still has to add up to the total
  const otherRows = stats
    .filter((s) => !knownKeys.has(s.service_key) && Number(s.total_cost_cents) > 0)
    .map((s) =>
      buildRow(
        {
          key: s.service_key,
          label: formatServiceKey(s.service_key),
          serviceKeys: [s.service_key],
          unit: "unit",
          unitPlural: "units",
        },
        stats
      )
    );
  if (otherRows.length) {
    const events = otherRows.reduce((s, r) => s + r.events, 0);
    categories.push({
      ...OTHER_CATEGORY,
      rows: otherRows,
      total: otherRows.reduce((s, r) => s + r.cost, 0),
      usageSummary: formatCount(events, ["request", "requests"]),
    });
  }

  return categories;
}

function channelTotal(channel: CostChannel, categories: CostCategory[]) {
  const sources = CHANNEL_COST_SOURCES[channel];
  const rows = categories.flatMap((c) => c.rows);
  const direct = rows.filter((r) => sources.types.includes(r.key)).reduce((s, r) => s + r.cost, 0);
  const ai = (rows.find((r) => r.key === "ai_content_generation")?.breakdown ?? [])
    .filter((b) => sources.aiChannels.includes(b.key))
    .reduce((s, b) => s + b.cost, 0);
  return direct + ai;
}

export function useUsageCosts(
  period: Ref<string | null | undefined>,
  dateRange: Ref<Array<Date | string> | null | undefined>
) {
  const dateParams = computed(() => getCostDateParams(unref(period), unref(dateRange)));
  const statsParams = computed(() => ({
    startDate: dateParams.value.startDate,
    endDate: dateParams.value.endDate,
  }));

  const query = useUsageStats(statsParams);

  const costs = computed(() => {
    const stats = ((query.data.value as UsageStatsResponse | null)?.stats ?? []) as UsageStatItem[];
    const categories = buildCostCategories(stats);
    const grandTotal = stats.reduce((sum, s) => sum + (Number(s.total_cost_cents) || 0), 0) / 100;
    const channels: Record<CostChannel, number> = {
      call: channelTotal("call", categories),
      email: channelTotal("email", categories),
      linkedin: channelTotal("linkedin", categories),
    };
    return {
      categories,
      channels,
      // AI chat, sequence generation and any usage not tied to a channel
      unattributed: Math.max(0, grandTotal - channels.call - channels.email - channels.linkedin),
      grandTotal,
    };
  });

  return {
    costs,
    dateParams,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    // useUsageStats answers null when the API reports a failure, so that counts as one too
    isError: computed(
      () => query.isError.value || (query.isSuccess.value && query.data.value == null)
    ),
    refetch: query.refetch,
  };
}
